<script setup>
import { computed } from "vue";

import { useWebsiteStore } from "@/stores/website";
import { storeToRefs } from "pinia";
import Spinner from "@/components/Spinner.vue";

const websiteStore = useWebsiteStore();
const {website} = storeToRefs(websiteStore);
const props = defineProps({
      root: Object,
      forceRootSelection: Boolean,
      parentPath: {
        type: String,
        default: ''
      },
      selectedPaths: Object
    }
)

// sorted children by name
const sortedChildren = computed(() => {
  return props.root.children
});

function nodeChildrenCount(children) {
  let count = 0;
  if (children) {
    count += children.filter((child) => child.page).length;
    for (const child of children) {
      count += nodeChildrenCount(child.children);
    }
  }
  return count;
}

function selectedNodeChildrenCount(node) {
  let count = 0;
  if (node.children) {
    count += node.children.filter((child) => child.page && child.selected).length;
    for (const child of node.children) {
      count += selectedNodeChildrenCount(child);
    }
  }
  return count;
}

function selectNode(root, selected) {
  if (root.page) {
    if (['pending', 'in_progress'].includes(root.page.status)) {
      return
    }
  }

  const index = props.selectedPaths.indexOf(root.page?.url)
  if (root.page) {
    if (selected) {
      if (index === -1) {
        props.selectedPaths.push(root.page.url)
      }
    } else {

      if (index !== -1) {
        props.selectedPaths.splice(index, 1)
      }
    }
  }
  if (root.children) {
    for (const child of root.children) {
      child.selected = selected;
      selectNode(child, selected);
    }
  }
}

function selectNodesCount(root, state) {
  let count = 0;
  if (root.selected === state) {
    count += 1
  }
  if (root.children) {
    for (const child of root.children) {
      count += selectNodesCount(child, state);
    }
  }
  return count
}

function openUrl(url){
  window.open(url, '_blank');

}

function nodeIssueCounts(nodes) {
  // count issues of all nodes and their children
  let issues = {
    highPriority: 0,
    mediumPriority: 0,
  };
  for (const node of nodes) {
    let page = node.page;
    if (page) {
      issues.highPriority += page.highPriority || 0;  // the node could be a path stub with no page
      issues.mediumPriority += page.mediumPriority || 0;
    }
    if (node.children) {
      let childIssues = nodeIssueCounts(node.children);
      issues.highPriority += childIssues.highPriority;
      issues.mediumPriority += childIssues.mediumPriority;
    }
  }
  return issues;
}

const issueCounts = computed(() => {
  return nodeIssueCounts(props.root.children);
});


function toggleEntry(root){
  if (['pending', 'in_progress'].includes(root.page.status)){
    return
  }
  if (props.forceRootSelection && root.fullPath === '/'){
    return
  }

  root.selected=!root.selected;selectNode(root, root.selected)
}

</script>
<template>

  <div class="seo-report">
    <div class="" :id="`id-collapse${root.fullPath}`" v-if="root.children.length > 0">

      <div class="seo-report-entry" :class="{[`structure-collapse`]: true}" data-bs-toggle="collapse"
           :data-bs-target="`#collapse${root.fullPath}`">
              <span class="checkbox">
                <input  type="checkbox" v-model="root.selected" :id="`checkbox${root.fullPath}`" value=""
                       class="form-check-input me-3" data-bs-toggle="collapse" data-bs-target
                       @change="selectNode(root, root.selected)"
                >
              </span>
        <i class="far fa-folder page-label-icon"></i>
        <a v-if="root.url" :href="root.url" target="_blank" title="External Link"><i
            class="fa fa-link page-label-icon"></i></a>
        <label class="treeview-dir" >/{{
            root.name
          }}</label>

        <span v-if="root.children.length > 0" class="more-entry" v-show="selectedNodeChildrenCount(root)>0">{{
            selectedNodeChildrenCount(root)
          }} selected pages</span>

        <span style="float:right;font-size:12px;cursor:pointer" class="text-muted">{{ nodeChildrenCount(root.children) }} pages</span>
      </div>


      <div :id="`collapse${root.fullPath}`" class=" collapse "
           :data-bs-parent="`#id-collapse${root.fullPath}`">
        <div class="ms-3">
          <div v-for="child in sortedChildren" :key="child.name">
            <Treeview :force-root-selection="forceRootSelection" :root="child" :parent-path="parentPath" :selected-paths="selectedPaths"/>
          </div>
        </div>

      </div>

    </div>
    <div v-else>
      <div class="seo-report-entry overflow-text-ellipsis" @click="toggleEntry(root)">

        <div class="">
          <span class="checkbox">
            <input :disabled="(props.forceRootSelection && root.fullPath === '/') || (['pending', 'in_progress'].includes(root.page.status))" type="checkbox" :id="`checkbox${root.fullPath}`"
                   class="form-check-input me-3"

                   v-model="root.selected" @change="selectNode(root, root.selected)">
          </span>
          <a  @click.prevent="openUrl(root.url)" :href="root.url" target="_blank" title="External Link"><i class="fa fa-link page-label-icon"></i></a>
          <label >
           <span v-if="root.fullPath === '/'">{{ root.url }}</span><span v-else>{{ root.fullPath }}</span></label>

          <span style="float:right;font-size:12px;cursor:pointer" class="text-muted" v-if="['pending', 'in_progress'].includes(root.page.status)">In Progress <spinner></spinner> </span>
  <span v-else-if="root.page.crawlDate" title="Last Checked" style="float:right;font-size:12px;cursor:pointer" class="text-muted">{{$filters.dateRelative(root.page.crawlDate) }}</span>

        </div>
      </div>
    </div>
  </div>
</template>
<style>
span.more-entry {
  color: rgba(108, 117, 125, 0.41);
  font-size: 10pt;
  margin-left: 10px;
}


.seo-report-entry {
  .treeview-dir {
    color: black;
  }

  .page-label-icon {
    color: gray;
    margin-right: 10px;
    font-size: 12px;
    cursor: pointer;
  }

  &:hover {
    background-color: #f8f9fa;
  }

  background-color: white;
  padding: 10px;
  border-bottom: 1px solid #f7f7f7;
}


</style>
